import { GripVertical, Plus, X } from "lucide-react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import type {
  OnsiteUpsellProductSelection,
  OnsiteUpsellRuleAuthoring,
  UpsellCondition,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import type { DraggableProvided } from "@hello-pangea/dnd";
import Stack from "@mui/material/Stack";
import { ConditionRow } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/ConditionRow";
import { EntityOption } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/entityOptions";
import { ProductSourceSelect } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/ProductSourceSelect";
import { createCondition } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/options";

interface UpsellRuleCardProps {
  rule: OnsiteUpsellRuleAuthoring;
  ruleIndex: number;
  location: ComponentPlacementLocation;
  collections: EntityOption[];
  tags: EntityOption[];
  onChange: (next: OnsiteUpsellRuleAuthoring) => void;
  onRemove: () => void;
  showRemove: boolean;
  dragProvided: DraggableProvided;
}

export const UpsellRuleCard = ({
  rule,
  ruleIndex,
  location,
  collections,
  tags,
  onChange,
  onRemove,
  showRemove,
  dragProvided,
}: UpsellRuleCardProps) => {
  const setConditions = (conditions: UpsellCondition[]) =>
    onChange({ ...rule, conditions });
  const setProducts = (products: OnsiteUpsellProductSelection) =>
    onChange({ ...rule, products });

  // The empty (always-on) rule shows a single synthetic "anything" row.
  const rows = rule.conditions.length ? rule.conditions : [null];

  const changeRow = (index: number, next: UpsellCondition | null) => {
    if (!rule.conditions.length) {
      // synthetic anything row -> becomes the first real condition
      setConditions(next ? [next] : []);
      return;
    }
    if (next === null) {
      setConditions(rule.conditions.filter((_, i) => i !== index));
      return;
    }
    setConditions(rule.conditions.map((c, i) => (i === index ? next : c)));
  };

  return (
    <Stack
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      direction="row"
      alignItems="flex-start"
      sx={{
        border: (theme) => `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        p: 2,
      }}
    >
      <IgIconButton
        size="small"
        {...dragProvided.dragHandleProps}
        aria-label="Reorder rule"
      >
        <GripVertical size={16} />
      </IgIconButton>

      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <IgTypography fontWeight={600}>IF</IgTypography>
          {showRemove && (
            <IgIconButton
              size="small"
              onClick={onRemove}
              aria-label="Remove rule"
            >
              <X size={16} />
            </IgIconButton>
          )}
        </Stack>

        <IgPaper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {rows.map((condition, index) => (
              <ConditionRow
                key={index}
                condition={condition}
                location={location}
                ruleIndex={ruleIndex}
                conditionIndex={index}
                collections={collections}
                tags={tags}
                onChange={(next) => changeRow(index, next)}
                onRemove={() => changeRow(index, null)}
                showRemove={rule.conditions.length > 0}
              />
            ))}
            <IgButton
              variant="text"
              color="primary"
              size="extraSmall"
              startIcon={<Plus size={16} />}
              onClick={() =>
                setConditions([
                  ...rule.conditions,
                  createCondition("collection")!,
                ])
              }
              sx={{ alignSelf: "flex-start" }}
            >
              AND Condition
            </IgButton>
          </Stack>
        </IgPaper>

        <IgTypography fontWeight={600}>THEN SHOW</IgTypography>
        <IgPaper sx={{ p: 2 }}>
          <ProductSourceSelect
            products={rule.products}
            ruleIndex={ruleIndex}
            collections={collections}
            tags={tags}
            onChange={setProducts}
          />
        </IgPaper>
      </Stack>
    </Stack>
  );
};
